import Add from '@mui/icons-material/Add'
import Summarize from '@mui/icons-material/Summarize'
import CheckCircle from '@mui/icons-material/CheckCircle'
import LocationOn from '@mui/icons-material/LocationOn'
import People from '@mui/icons-material/People'
import Settings from '@mui/icons-material/Settings'
import Business from '@mui/icons-material/Business'
import Person from '@mui/icons-material/Person'
import Email from '@mui/icons-material/Email'
import Phone from '@mui/icons-material/Phone'
import Send from '@mui/icons-material/Send'
import {
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material'
import { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { GenericHeaderBackButton } from 'src/consumer/components/UserActivityPage/MyPlanComponents'
import { ROLE_TYPES } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { postMPDataWithParams } from 'src/mentor/services'
import ClinicHeader from '../ClinicHeader'
import { AvailabilityPageContainer } from '../ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { getStaffInviteBodyFromUserContact } from './utils'

// Modern styled components
const PageContainer = styled(AvailabilityPageContainer)`
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  min-height: 100vh;
  padding-bottom: 40px;
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const SectionCard = styled(Card)`
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(116, 80, 170, 0.1);
  margin-bottom: 32px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 32px rgba(116, 80, 170, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    margin-bottom: 24px;
  }
`

const SectionHeader = styled(Box)`
  padding: 24px 32px;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%);
  border-bottom: 1px solid rgba(116, 80, 170, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
  }
`

const SectionTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const SectionContent = styled(Box)`
  padding: 32px;
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const ProgressStep = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'completed',
})`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 32px;
  padding: 20px;
  border-radius: 16px;
  background: ${(props) =>
    props.completed
      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)'};
  border: 2px solid
    ${(props) => (props.completed ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)')};
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
`

const StepIcon = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'completed',
})`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) =>
    props.completed
      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
  color: white;
  box-shadow: 0 4px 12px
    ${(props) => (props.completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)')};
`

const StepContent = styled(Box)`
  flex: 1;
`

const StepTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const StepDescription = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
`

const ActionButton = styled(Button)`
  text-transform: none;
  border-radius: 10px;
  padding: 8px 20px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(116, 80, 170, 0.2);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(116, 80, 170, 0.3);
    transform: translateY(-2px);
  }
`

const WorkforceGrid = styled(Grid)`
  margin-top: 24px;
`

const RoleCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid rgba(116, 80, 170, 0.15);
  transition: all 0.3s ease;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  margin-bottom: 20px;

  &:hover {
    border-color: rgba(116, 80, 170, 0.3);
    box-shadow: 0 4px 16px rgba(116, 80, 170, 0.12);
    transform: translateY(-2px);
  }
`

const RoleHeader = styled(Box)`
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.03) 100%);
  border-bottom: 1px solid rgba(116, 80, 170, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const RoleTitle = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusBadge = styled(Chip)`
  height: 24px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
`

const StaffList = styled(Box)`
  padding: 16px 20px;
`

const StaffItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
  }
`

const StaffInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const StaffDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StaffName = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const StaffContact = styled(Typography)`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
`

const ClinicLocationCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid rgba(16, 185, 129, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const ClinicCardContent = styled(CardContent)`
  padding: 24px !important;
`

const ClinicName = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`

const ClinicAddress = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`

const AddressLine = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyState = styled(Box)`
  text-align: center;
  padding: 48px 24px;
  color: #9ca3af;
`

const EmptyStateText = styled(Typography)`
  font-size: 16px;
  color: #6b7280;
  margin-top: 12px;
`

export default function ClinicBrandHomepage() {
  const navigate = useNavigate()
  const { clinicBrandId } = useParams()
  const { chosenRoleFlow, ownerData, assistantData, refreshOwnersAssociatedBrandsData } =
    useContext(ClinicDataContext)

  async function sendInvite(contact) {
    const resp = await postMPDataWithParams(
      '/api/secure/cp/send-invitation',
      getStaffInviteBodyFromUserContact(contact, clinicBrandId),
    )
    if (resp.status === 200) {
      const respCode = resp.data.code
      switch (respCode) {
        case 4000:
        case 4003:
        case 4001:
          toast.error('Something went wrong. Contact the developers.')
          break
        case 4170:
          toast.info('This invitation has already been accepted by the user')
          break
        case 4030:
          toast.info('Invitation sending for this user is in cooldown.')
          break
        default:
          toast.success('Invitation Sent Successfully')
      }
    } else {
      toast.error('Something went wrong!')
    }
  }

  if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
    const clinicsList = (ownerData?.associatedClinicsData || [])?.filter(
      (item) => item?.clinicBrandId === clinicBrandId,
    )
    const thisClinicBrandData = ownerData?.associatedClinicBrandsData?.find(
      (item) => item?.id === clinicBrandId,
    )

    // Calculate workforce completion status
    const hasAdmins = !!(
      thisClinicBrandData?.workforce?.admins?.length ||
      thisClinicBrandData?.prospects?.admins?.length
    )
    const hasDoctors = !!(
      thisClinicBrandData?.workforce?.doctors?.length ||
      thisClinicBrandData?.prospects?.doctors?.length
    )
    const hasTherapists = !!(
      thisClinicBrandData?.workforce?.therapists?.length ||
      thisClinicBrandData?.prospects?.therapists?.length
    )
    const hasAssistants = !!(
      thisClinicBrandData?.workforce?.clinicAssistants?.length ||
      thisClinicBrandData?.prospects?.clinicAssistants?.length
    )
    const workforceComplete = hasAdmins && hasDoctors && hasTherapists && hasAssistants

    return (
      <PageContainer>
        <ClinicHeader
          heading={thisClinicBrandData?.brandName || thisClinicBrandData?.title || ''}
        />
        <GenericHeaderBackButton
          clickfunction={() => window.history.back()}
          styles={{ margin: '20px' }}
        />
        <ContentWrapper>
          {/* Progress Section */}
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <CheckCircle sx={{ color: '#10b981', fontSize: 28 }} />
                Setup Progress
              </SectionTitle>
              {getAllRolesInBoolean().isClinicOwner && getAllRolesInBoolean().isDoctor && (
                <ActionButton
                  variant="contained"
                  startIcon={<Summarize />}
                  onClick={() => navigate('payout-reports')}
                  sx={{
                    background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a3d88 0%, #4a2f6f 100%)',
                    },
                  }}
                >
                  View Payout Reports
                </ActionButton>
              )}
            </SectionHeader>
            <SectionContent>
              {/* Brand Profile Step */}
              <ProgressStep completed={true}>
                <StepIcon completed={true}>
                  <CheckCircle sx={{ fontSize: 28 }} />
                </StepIcon>
                <StepContent>
                  <StepTitle>Brand Profile Settings</StepTitle>
                  <StepDescription>
                    Configure your brand information and details
                  </StepDescription>
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('update-brand-details')}
                    sx={{
                      borderColor: '#7450aa',
                      color: '#7450aa',
                      '&:hover': {
                        borderColor: '#5a3d88',
                        background: 'rgba(116, 80, 170, 0.05)',
                      },
                    }}
                  >
                    Update Brand Details
                  </ActionButton>
                </StepContent>
              </ProgressStep>

              {/* Workforce Step */}
              <ProgressStep completed={workforceComplete}>
                <StepIcon completed={workforceComplete}>
                  {workforceComplete ? (
                    <CheckCircle sx={{ fontSize: 28 }} />
                  ) : (
                    <People sx={{ fontSize: 28 }} />
                  )}
                </StepIcon>
                <StepContent>
                  <StepTitle>Workforce Management</StepTitle>
                  <StepDescription>
                    Add and manage your team members: Admins, Doctors, Therapists, and Assistants
                  </StepDescription>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <ActionButton
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('update-brand-people')}
                      sx={{
                        borderColor: '#7450aa',
                        color: '#7450aa',
                        '&:hover': {
                          borderColor: '#5a3d88',
                          background: 'rgba(116, 80, 170, 0.05)',
                        },
                      }}
                    >
                      Manage Workforce
                    </ActionButton>
                    {!workforceComplete && (
                      <Chip
                        label="Incomplete"
                        color="warning"
                        size="small"
                        sx={{ height: 32, fontWeight: 600 }}
                      />
                    )}
                  </Box>
                </StepContent>
              </ProgressStep>

              {/* Clinic Locations Step */}
              <ProgressStep completed={!!clinicsList?.length}>
                <StepIcon completed={!!clinicsList?.length}>
                  {clinicsList?.length ? (
                    <CheckCircle sx={{ fontSize: 28 }} />
                  ) : (
                    <LocationOn sx={{ fontSize: 28 }} />
                  )}
                </StepIcon>
                <StepContent sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <StepTitle>Manage your clinic locations and branches</StepTitle>
                      <StepDescription>
                        Add and manage your clinic locations and branches
                      </StepDescription>
                    </Box>
                    <ActionButton
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => navigate('new-clinic')}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        },
                      }}
                    >
                      {!clinicsList?.length ? 'Add Primary Branch' : 'Add Another Branch'}
                    </ActionButton>
                  </Box>
                  
                  {/* Clinic Location Tiles */}
                  {clinicsList?.length > 0 ? (
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      {clinicsList.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <ClinicLocationCard onClick={() => navigate(item?.id)}>
                            <ClinicCardContent>
                              <ClinicName>
                                {item?.name}
                                {item?.kycStatus === 'pending' && (
                                  <Chip
                                    label="KYC Pending"
                                    color="warning"
                                    size="small"
                                    sx={{ fontSize: '10px', height: '22px', fontWeight: 600 }}
                                  />
                                )}
                              </ClinicName>
                              <ClinicAddress>
                                {item?.location?.address && (
                                  <AddressLine>
                                    <LocationOn sx={{ fontSize: 16, color: '#6b7280' }} />
                                    {item?.location?.address}
                                  </AddressLine>
                                )}
                                {item?.location?.city && (
                                  <AddressLine>{item?.location?.city}</AddressLine>
                                )}
                              </ClinicAddress>
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <ActionButton
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #5a3d88 0%, #4a2f6f 100%)',
                                    },
                                  }}
                                >
                                  Open Location
                                </ActionButton>
                              </Box>
                            </ClinicCardContent>
                          </ClinicLocationCard>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <EmptyState sx={{ mt: 3 }}>
                      <LocationOn sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                      <EmptyStateText>
                        No clinic locations yet. Add your first branch to get started.
                      </EmptyStateText>
                    </EmptyState>
                  )}
                </StepContent>
              </ProgressStep>
            </SectionContent>
          </SectionCard>

          {/* Workforce Section */}
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <People sx={{ color: '#7450aa', fontSize: 28 }} />
                Workforce Overview
              </SectionTitle>
              <ActionButton
                variant="outlined"
                size="small"
                onClick={() => navigate('update-brand-people')}
                sx={{
                  borderColor: '#7450aa',
                  color: '#7450aa',
                  '&:hover': {
                    borderColor: '#5a3d88',
                    background: 'rgba(116, 80, 170, 0.05)',
                  },
                }}
              >
                Manage All
              </ActionButton>
            </SectionHeader>
            <SectionContent>
              <WorkforceGrid container spacing={3}>
                {/* Admins */}
                <Grid item xs={12} md={6}>
                  <RoleCard>
                    <RoleHeader>
                      <RoleTitle>
                        <Business sx={{ fontSize: 20, color: '#7450aa' }} />
                        Admins
                      </RoleTitle>
                      <StatusBadge
                        label={
                          thisClinicBrandData?.workforce?.admins?.length > 0
                            ? `${thisClinicBrandData?.workforce?.admins?.length} Active`
                            : 'None'
                        }
                        color={
                          thisClinicBrandData?.workforce?.admins?.length > 0 ? 'success' : 'default'
                        }
                      />
                    </RoleHeader>
                    <StaffList>
                      <StaffViewer
                        staffData={thisClinicBrandData?.workforce?.admins}
                        type="Active"
                        staffMultiTitle="Admins"
                        staffSingleTitle="Admin"
                        role="ROLE_CLINIC_ADMIN"
                        sendStaffInvite={sendInvite}
                      />
                      <StaffViewer
                        staffData={thisClinicBrandData?.prospects?.admins}
                        type="Pending"
                        isProspect
                        staffMultiTitle="Admins"
                        staffSingleTitle="Admin"
                        role="ROLE_CLINIC_ADMIN"
                        sendStaffInvite={sendInvite}
                      />
                    </StaffList>
                  </RoleCard>
                </Grid>

                {/* Doctors */}
                <Grid item xs={12} md={6}>
                  <RoleCard>
                    <RoleHeader>
                      <RoleTitle>
                        <Person sx={{ fontSize: 20, color: '#7450aa' }} />
                        Doctors
                      </RoleTitle>
                      <StatusBadge
                        label={
                          thisClinicBrandData?.workforce?.doctors?.length > 0
                            ? `${thisClinicBrandData?.workforce?.doctors?.length} Active`
                            : 'None'
                        }
                        color={
                          thisClinicBrandData?.workforce?.doctors?.length > 0 ? 'success' : 'default'
                        }
                      />
                    </RoleHeader>
                    <StaffList>
                      <StaffViewer
                        staffData={thisClinicBrandData?.workforce?.doctors}
                        type="Active"
                        staffMultiTitle="Doctors"
                        staffSingleTitle="Doctor"
                        role="ROLE_DOCTOR"
                        sendStaffInvite={sendInvite}
                      />
                      <StaffViewer
                        staffData={thisClinicBrandData?.prospects?.doctors}
                        type="Pending"
                        isProspect
                        staffMultiTitle="Doctors"
                        staffSingleTitle="Doctor"
                        role="ROLE_DOCTOR"
                        sendStaffInvite={sendInvite}
                      />
                    </StaffList>
                  </RoleCard>
                </Grid>

                {/* Therapists */}
                <Grid item xs={12} md={6}>
                  <RoleCard>
                    <RoleHeader>
                      <RoleTitle>
                        <Person sx={{ fontSize: 20, color: '#7450aa' }} />
                        Therapists
                      </RoleTitle>
                      <StatusBadge
                        label={
                          thisClinicBrandData?.workforce?.therapists?.length > 0
                            ? `${thisClinicBrandData?.workforce?.therapists?.length} Active`
                            : 'None'
                        }
                        color={
                          thisClinicBrandData?.workforce?.therapists?.length > 0
                            ? 'success'
                            : 'default'
                        }
                      />
                    </RoleHeader>
                    <StaffList>
                      <StaffViewer
                        staffData={thisClinicBrandData?.workforce?.therapists}
                        type="Active"
                        staffMultiTitle="Therapists"
                        staffSingleTitle="Therapist"
                        role="ROLE_THERAPIST"
                        sendStaffInvite={sendInvite}
                      />
                      <StaffViewer
                        staffData={thisClinicBrandData?.prospects?.therapists}
                        type="Pending"
                        isProspect
                        staffMultiTitle="Therapists"
                        staffSingleTitle="Therapist"
                        role="ROLE_THERAPIST"
                        sendStaffInvite={sendInvite}
                      />
                    </StaffList>
                  </RoleCard>
                </Grid>

                {/* Assistants */}
                <Grid item xs={12} md={6}>
                  <RoleCard>
                    <RoleHeader>
                      <RoleTitle>
                        <People sx={{ fontSize: 20, color: '#7450aa' }} />
                        Assistants
                      </RoleTitle>
                      <StatusBadge
                        label={
                          thisClinicBrandData?.workforce?.clinicAssistants?.length > 0
                            ? `${thisClinicBrandData?.workforce?.clinicAssistants?.length} Active`
                            : 'None'
                        }
                        color={
                          thisClinicBrandData?.workforce?.clinicAssistants?.length > 0
                            ? 'success'
                            : 'default'
                        }
                      />
                    </RoleHeader>
                    <StaffList>
                      <StaffViewer
                        staffData={thisClinicBrandData?.workforce?.clinicAssistants}
                        type="Active"
                        staffMultiTitle="Assistants"
                        staffSingleTitle="Assistant"
                        role="ROLE_CLINIC_ASSISTANTS"
                        sendStaffInvite={sendInvite}
                      />
                      <StaffViewer
                        staffData={thisClinicBrandData?.prospects?.clinicAssistants}
                        type="Pending"
                        isProspect
                        staffMultiTitle="Assistants"
                        staffSingleTitle="Assistant"
                        role="ROLE_CLINIC_ASSISTANTS"
                        sendStaffInvite={sendInvite}
                      />
                    </StaffList>
                  </RoleCard>
                </Grid>
              </WorkforceGrid>
            </SectionContent>
          </SectionCard>
        </ContentWrapper>
      </PageContainer>
    )
  }
}

function StaffViewer({
  staffData = [],
  isProspect = false,
  staffSingleTitle,
  staffMultiTitle,
  type,
  limit: dataLimit,
  sendStaffInvite,
  role,
}) {
  const limit = dataLimit ?? Number.MAX_SAFE_INTEGER
  const showDotString = staffData?.length > limit
  const exceededDataNumber = staffData?.length - limit

  if (!Array.isArray(staffData) || staffData?.length === 0) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No {type.toLowerCase()} {staffMultiTitle.toLowerCase()}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {staffData?.slice(0, limit)?.map((item, index) => (
        <StaffItemComponent
          key={index}
          contact={item}
          role={role}
          sendStaffInvite={sendStaffInvite}
          index={index}
          isProspect={isProspect}
          type={type}
        />
      ))}
      {showDotString && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
          + {exceededDataNumber} more
        </Typography>
      )}
    </Box>
  )
}

function StaffItemComponent({ contact, index, isProspect, role, sendStaffInvite, type }) {
  const [sending, setSending] = useState(false)

  async function sendInvite() {
    setSending(true)
    await sendStaffInvite({ ...(contact || {}), role: role, userRefId: contact?.tempId })
    setSending(false)
  }

  return (
    <StaffItem>
      <StaffInfo>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {contact?.title?.charAt(0)?.toUpperCase() || '?'}
        </Box>
        <StaffDetails>
          <StaffName>{contact?.title || 'Unknown'}</StaffName>
          <StaffContact>
            {contact?.email && (
              <>
                <Email sx={{ fontSize: 12 }} />
                {contact?.email}
              </>
            )}
            {contact?.mobile && !contact?.email && (
              <>
                <Phone sx={{ fontSize: 12 }} />
                {contact?.mobile}
              </>
            )}
          </StaffContact>
        </StaffDetails>
      </StaffInfo>
      {isProspect && (
        <Tooltip title="Resend invitation">
          <IconButton
            size="small"
            onClick={sendInvite}
            disabled={sending}
            sx={{
              background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a3d88 0%, #4a2f6f 100%)',
              },
            }}
          >
            {sending ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <Send sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </StaffItem>
  )
}
